import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Heading } from "./NKDaily";
import { AvatarIcon, CountryFlag } from "@/lib/identity";
import { CheckCircle, X, ShieldCheck, ShieldWarning } from "@phosphor-icons/react";

const STATUS_TABS = [
  { v: "pending", l: "Pending" },
  { v: "approved", l: "Approved" },
  { v: "rejected", l: "Rejected" },
  { v: "all", l: "All" },
];

const TIER_LABEL = { 3: "Top-finisher (Tier 3)" };

export default function Verifications() {
  const [tab, setTab] = useState("pending");
  const [data, setData] = useState({ rows: [], counts: {} });
  const [busy, setBusy] = useState(null);

  const load = () => api.get(`/verification/admin/queue?status=${tab}`)
    .then((r) => setData(r.data))
    .catch(() => setData({ rows: [], counts: {} }));
  useEffect(() => { load(); }, [tab]);

  const decide = async (request_id, decision) => {
    const reason = decision === "reject" ? prompt("Reason for rejection (visible to player):", "") : null;
    if (decision === "reject" && !reason) return;
    setBusy(request_id);
    try {
      await api.post(`/verification/admin/decide/${request_id}`, { decision, reason });
      toast.success(decision === "approve" ? "Approved" : "Rejected");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-6">
      <Heading
        title="Verifications"
        subtitle="3-tier verification ladder. Tier 1 = email verified (auto). Tier 2 = activity gate (auto). Tier 3 = top-finisher review (manual)."
      />

      <div className="grid grid-cols-3 gap-3">
        <Card label="Pending" value={data.counts?.pending || 0} tone="warn" />
        <Card label="Approved" value={data.counts?.approved || 0} tone="good" />
        <Card label="Rejected" value={data.counts?.rejected || 0} tone="mute" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {STATUS_TABS.map((t) => (
          <button key={t.v} onClick={() => setTab(t.v)}
                  data-testid={`verif-tab-${t.v}`}
                  className={`px-3 py-1.5 text-xs border rounded-sm ${
                    tab === t.v ? "bg-nk-gold text-nk-bg border-nk-gold font-medium" : "border-nk-line text-nk-mute hover:text-nk-text"
                  }`}>{t.l}</button>
        ))}
      </div>

      <div className="space-y-3">
        {data.rows.length === 0 ? (
          <div className="nk-card p-10 text-center text-nk-mute text-sm">No requests in this status.</div>
        ) : data.rows.map((r) => (
          <article key={r.request_id} data-testid={`verif-row-${r.request_id}`}
                   className="nk-card p-4 flex flex-col sm:flex-row gap-4 sm:items-center">
            <AvatarIcon id={r.user?.avatar} size={42} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-medium">{r.user?.username || "Player"}</p>
                {r.user?.country && <CountryFlag country={r.user.country} size={12} />}
                <span className={`text-[10px] px-1.5 py-0.5 rounded-sm uppercase tracking-wider ${
                  r.tier === 4 ? "bg-nk-gold/15 text-nk-gold border border-nk-gold/30" : "bg-nk-accent/15 text-nk-accent border border-nk-accent/30"
                }`}>{TIER_LABEL[r.tier]}</span>
              </div>
              <p className="text-xs text-nk-mute mt-1">
                {r.full_legal_name} · {r.id_document_type.replace("_", " ")} <span className="font-mono">{r.id_document_number}</span>
                {r.phone && <> · {r.phone}</>} · Residency <span className="font-mono">{r.residency_country}</span>
              </p>
              {r.notes && <p className="text-xs text-nk-dim mt-1">"{r.notes}"</p>}
              <p className="text-[10px] text-nk-dim mt-2 font-mono">Submitted {r.submitted_at?.slice(0, 16).replace("T", " ")}</p>
              {r.status === "rejected" && r.reason && (
                <p className="text-xs text-nk-bad mt-1">Rejected: {r.reason}</p>
              )}
            </div>
            {r.status === "pending" && (
              <div className="flex gap-2 shrink-0">
                <button disabled={busy === r.request_id} onClick={() => decide(r.request_id, "approve")}
                        data-testid={`verif-approve-${r.request_id}`}
                        className="px-3 py-1.5 bg-nk-good text-nk-bg text-xs font-medium rounded-sm inline-flex items-center gap-1.5 disabled:opacity-40">
                  <CheckCircle size={12} weight="bold" /> Approve
                </button>
                <button disabled={busy === r.request_id} onClick={() => decide(r.request_id, "reject")}
                        data-testid={`verif-reject-${r.request_id}`}
                        className="px-3 py-1.5 border border-nk-line text-xs text-nk-mute hover:text-nk-bad rounded-sm inline-flex items-center gap-1.5 disabled:opacity-40">
                  <X size={12} weight="bold" /> Reject
                </button>
              </div>
            )}
            {r.status === "approved" && (
              <span className="text-[10px] uppercase tracking-wider text-nk-good inline-flex items-center gap-1.5">
                <ShieldCheck size={14} weight="duotone" /> Approved
              </span>
            )}
            {r.status === "rejected" && (
              <span className="text-[10px] uppercase tracking-wider text-nk-bad inline-flex items-center gap-1.5">
                <ShieldWarning size={14} weight="duotone" /> Rejected
              </span>
            )}
          </article>
        ))}
      </div>
    </div>
  );
}

function Card({ label, value, tone }) {
  const cls = tone === "good" ? "text-nk-good" : tone === "warn" ? "text-nk-warn" : "text-nk-mute";
  return (
    <div className="nk-card p-4">
      <p className="text-[10px] uppercase tracking-wider text-nk-mute mb-1.5">{label}</p>
      <p className={`font-display text-2xl ${cls}`}>{value}</p>
    </div>
  );
}
